import {
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  CornerRightDown,
  Grid3x3,
  Hammer,
  Layers,
  Plus,
  Tag,
  Upload,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import type { TowerId } from '@obraplan/core/domain/shared/ids';
import { ok, type Result } from '@obraplan/core/domain/shared/result';
import type { Floor } from '@obraplan/core/domain/structure/floor';
import type { Tower } from '@obraplan/core/domain/structure/tower';
import type { AppError } from '@obraplan/core/application/errors/app-error';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SegmentedBar } from '@/components/ui/segmented-bar';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import {
  useContainer,
  usePermission,
  usePermissionGlobally,
  useSessionState,
} from '@/features/session/use-session';
import { StructureTree } from '@/features/structure/StructureTree';
import {
  ApplyDisciplinesDialog,
  CreateDisciplineDialog,
  CreateFloorDialog,
  CreateFloorRangeDialog,
  CreateTowerDialog,
  RenameNodeDialog,
  type DisciplineSubmit,
  type FloorRangeSubmit,
  type FloorSubmit,
  type RenameSubmit,
} from '@/features/structure/StructureDialogs';
import type { LoteDeLigacao } from '@/features/structure/grade-de-disciplinas';
import { CreateMaterialDialog, ItemDialog, type ItemSubmit } from '@/features/structure/ItemDialog';
import { StructureImportDialog } from '@/features/structure/StructureImportDialog';
import type { StructureColumnMapping } from '@obraplan/core/domain/structure/structure-import';
import type { StructureImportPreview } from '@obraplan/core/application/use-cases/structure/structure-import-use-cases';
import {
  allExpandableIds,
  findActivityLabel,
  findItemTarget,
  findRenameTarget,
  type FlatTreeNode,
  type ItemTarget,
  type RenameTarget,
} from '@/features/structure/tree-model';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

const EXPANDED_STORAGE_PREFIX = 'obraplan.structure.expanded.';

/**
 * Estrutura da obra — a árvore `OBRA → TORRE → PAVIMENTO → DISCIPLINA → ITEM`, com cada
 * pavimento uma única vez: a normalização exigida, materializada na tela.
 */

/** `1 torre` · `2 torres`. Contagem com plural, que a importação exibe várias de uma vez. */
function plural(count: number, singular: string, many: string): string {
  return `${String(count)} ${count === 1 ? singular : many}`;
}

export function StructurePage() {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const canManage = usePermission(PERMISSION.STRUCTURE_MANAGE);
  // Disciplina global sai só de papel de sistema, não do papel desta obra (RN-133).
  const canManageGlobally = usePermissionGlobally(PERMISSION.STRUCTURE_MANAGE);
  const canEditSchedule = usePermission(PERMISSION.SCHEDULE_EDIT);
  const canManageMaterials = usePermission(PERMISSION.MATERIALS_MANAGE);

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [towerOpen, setTowerOpen] = useState(false);
  const [floorRangeTower, setFloorRangeTower] = useState<{ id: TowerId; name: string } | undefined>(
    undefined,
  );
  const [floorOpen, setFloorOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [disciplineOpen, setDisciplineOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<RenameTarget | undefined>(undefined);
  // Lançar item: guarda a atividade; editar: guarda o item já resolvido.
  const [itemActivity, setItemActivity] = useState<{ id: string; label: string } | undefined>(
    undefined,
  );
  const [itemTarget, setItemTarget] = useState<ItemTarget | undefined>(undefined);
  const [materialOpen, setMaterialOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState<StructureImportPreview | undefined>(undefined);
  // Os bytes ficam aqui para que trocar o mapeamento não exija reabrir o arquivo.
  const [importFile, setImportFile] = useState<{ bytes: Uint8Array; name: string } | undefined>(
    undefined,
  );
  const [deleteTarget, setDeleteTarget] = useState<FlatTreeNode | undefined>(undefined);
  const [deleteImpact, setDeleteImpact] = useState<string>('');

  const view = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getProjectStructure.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );

  const project = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getProject.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );

  // O estado de expansão é preferência de navegação, não dado de negócio: fica no
  // armazenamento local do navegador, separado por obra.
  useEffect(() => {
    if (activeProjectId === undefined) return;
    const stored = globalThis.localStorage.getItem(EXPANDED_STORAGE_PREFIX + activeProjectId);
    setExpanded(new Set(stored === null ? [] : (JSON.parse(stored) as string[])));
  }, [activeProjectId]);

  const persistExpanded = useCallback(
    (next: ReadonlySet<string>) => {
      setExpanded(next);
      if (activeProjectId !== undefined) {
        globalThis.localStorage.setItem(
          EXPANDED_STORAGE_PREFIX + activeProjectId,
          JSON.stringify([...next]),
        );
      }
    },
    [activeProjectId],
  );

  const toggle = useCallback(
    (id: string) => {
      const next = new Set(expanded);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      persistExpanded(next);
    },
    [expanded, persistExpanded],
  );

  const tree = view !== undefined && view.ok ? view.value.tree : undefined;
  const disciplines = view !== undefined && view.ok ? view.value.availableDisciplines : [];
  const materials = view !== undefined && view.ok ? view.value.materials : [];
  const units = view !== undefined && view.ok ? view.value.units : [];

  const expandAll = useCallback(() => {
    if (tree === undefined) return;
    persistExpanded(new Set(allExpandableIds(tree)));
  }, [tree, persistExpanded]);

  const collapseAll = useCallback(() => persistExpanded(new Set()), [persistExpanded]);

  const firstTower = useMemo(
    () =>
      tree === undefined || tree.towers[0] === undefined
        ? undefined
        : { id: tree.towers[0].tower.id, name: tree.towers[0].tower.name },
    [tree],
  );

  // ── Comandos ────────────────────────────────────────────────────────────────
  const [runImportPreview, importPreviewState] = useCommand(
    (input: {
      bytes: Uint8Array;
      fileName: string;
      mapping?: StructureColumnMapping;
      createMissingDisciplines?: boolean;
    }) =>
      container.previewStructureImport.execute({ ...input, projectId: activeProjectId! }, session!),
    { silent: true, onSuccess: (value) => setImportPreview(value) },
  );

  const [confirmStructureImport, importConfirmState] = useCommand(
    (createMissingDisciplines: boolean) =>
      container.confirmStructureImport.execute(
        {
          projectId: activeProjectId!,
          rows: importPreview?.rows ?? [],
          fileName: importPreview?.fileName ?? '',
          createMissingDisciplines,
        },
        session!,
      ),
    {
      successMessage: (result) =>
        [
          plural(result.activitiesCreated, 'atividade criada', 'atividades criadas'),
          plural(result.towersCreated, 'torre', 'torres'),
          plural(result.floorsCreated, 'pavimento', 'pavimentos'),
          ...(result.disciplinesCreated > 0
            ? [plural(result.disciplinesCreated, 'disciplina', 'disciplinas')]
            : []),
        ].join(' · ') + '.',
      silent: true,
      onSuccess: () => {
        setImportOpen(false);
        setImportPreview(undefined);
        setImportFile(undefined);
      },
    },
  );

  const handleImportFile = (file: File): void => {
    void file.arrayBuffer().then((buffer) => {
      const bytes = new Uint8Array(buffer);
      setImportFile({ bytes, name: file.name });
      void runImportPreview({ bytes, fileName: file.name });
    });
  };

  const [createTower, towerState] = useCommand(
    (name: string) => container.createTower.execute({ projectId: activeProjectId, name }, session!),
    { successMessage: 'Torre criada.', silent: true, onSuccess: () => setTowerOpen(false) },
  );

  const [createFloorRange, floorRangeState] = useCommand(
    (input: FloorRangeSubmit) =>
      container.createFloorRange.execute({ ...input, towerId: floorRangeTower?.id }, session!),
    {
      successMessage: (result) =>
        result.skipped.length === 0
          ? `${String(result.created.length)} pavimentos criados.`
          : `${String(result.created.length)} criados · ${String(result.skipped.length)} já existiam.`,
      silent: true,
      onSuccess: () => setFloorRangeTower(undefined),
    },
  );

  const [createFloor, floorState] = useCommand(
    (input: FloorSubmit) =>
      container.createFloor.execute(
        {
          towerId: input.towerId,
          name: input.name,
          level: input.level,
          ...(input.code === undefined ? {} : { code: input.code }),
        },
        session!,
      ),
    {
      successMessage: (created) => `Pavimento "${created.name}" criado.`,
      silent: true,
      onSuccess: () => setFloorOpen(false),
    },
  );

  // A grade manda lotes: as disciplinas marcadas nos mesmos pavimentos vão juntas. Um
  // aviso só no fim, com a soma — e a primeira recusa para tudo, sem meio lote aplicado.
  const [applyDisciplinesToFloors, applyState] = useCommand(
    async (lotes: readonly LoteDeLigacao[]) => {
      let created = 0;
      let skipped = 0;
      for (const lote of lotes) {
        const resultado = await container.applyDisciplinesToFloors.execute(
          { floorIds: [...lote.floorIds], disciplineIds: [...lote.disciplineIds] },
          session!,
        );
        if (!resultado.ok) return resultado;
        created += resultado.value.created;
        skipped += resultado.value.skipped;
      }
      return ok({ created, skipped });
    },
    {
      successMessage: (result) =>
        result.skipped === 0
          ? `${String(result.created)} atividades criadas.`
          : `${String(result.created)} criadas · ${String(result.skipped)} já existiam.`,
      silent: true,
      onSuccess: () => setApplyOpen(false),
    },
  );

  const [renameNode, renameState] = useCommand(
    (target: RenameTarget, input: RenameSubmit): Promise<Result<Tower | Floor, AppError>> =>
      target.type === 'TOWER'
        ? container.renameTower.execute(
            { towerId: target.id, name: input.name, code: input.code },
            session!,
          )
        : container.renameFloor.execute(
            { floorId: target.id, name: input.name, code: input.code, level: input.level },
            session!,
          ),
    {
      successMessage: 'Nome atualizado.',
      silent: true,
      onSuccess: () => setRenameTarget(undefined),
    },
  );

  const [createDiscipline, disciplineState] = useCommand(
    // Alcance global manda `projectId` ausente — é isso que o domínio lê como
    // "disciplina do sistema" (RN-007).
    ({ global, ...input }: DisciplineSubmit) =>
      container.createDiscipline.execute(
        {
          ...input,
          ...(global ? {} : { projectId: activeProjectId }),
        },
        session!,
      ),
    {
      successMessage: (created) =>
        created.projectId === undefined
          ? `Disciplina "${created.name}" criada para todas as obras.`
          : `Disciplina "${created.name}" criada nesta obra.`,
      silent: true,
      onSuccess: () => setDisciplineOpen(false),
    },
  );

  const [saveItem, itemState] = useCommand(
    (input: ItemSubmit) =>
      itemTarget === undefined
        ? container.addActivityItem.execute({ ...input, activityId: itemActivity?.id }, session!)
        : container.updateActivityItem.execute({ ...input, entryId: itemTarget.entryId }, session!),
    {
      successMessage: (result) =>
        result.warnings.length === 0
          ? 'Item salvo.'
          : `Item salvo — ${result.warnings.map((warning) => warning.message).join(' ')}`,
      silent: true,
      onSuccess: () => {
        setItemActivity(undefined);
        setItemTarget(undefined);
      },
    },
  );

  const [createMaterial, materialState] = useCommand(
    (input: { code: string; name: string; unitId: string; category: string | undefined }) =>
      container.createMaterial.execute(input, session!),
    {
      successMessage: 'Material criado no catálogo.',
      silent: true,
      onSuccess: () => setMaterialOpen(false),
    },
  );

  const [deleteNode] = useCommand(
    (node: FlatTreeNode, cascade: boolean) =>
      container.deleteStructureNode.execute(
        { nodeType: node.type, nodeId: node.id, cascade },
        session!,
      ),
    {
      successMessage: 'Item removido.',
      onSuccess: () => setDeleteTarget(undefined),
    },
  );

  const openDelete = useCallback(
    async (node: FlatTreeNode) => {
      // O impacto vem por endpoint próprio, e não por um segundo método do caso de uso
      // de exclusão: pela rede, o que existe do outro lado é `execute`. A confirmação
      // segue mesmo sem ele — errar o detalhe é aceitável, travar a exclusão não é.
      const previewed =
        session === undefined
          ? undefined
          : await container.previewStructureNodeImpact.execute(
              { nodeType: node.type, nodeId: node.id },
              session,
            );
      const impact = previewed?.ok === true ? previewed.value : undefined;
      const parts: string[] = [];
      if (impact !== undefined) {
        if (impact.floors > 0) parts.push(`${String(impact.floors)} pavimento(s)`);
        if (impact.activities > 0) parts.push(`${String(impact.activities)} atividade(s)`);
        if (impact.items > 0) parts.push(`${String(impact.items)} item(ns) de cronograma`);
        if (impact.scheduleItems > 0) {
          parts.push(`${String(impact.scheduleItems)} item(ns) do cronograma por mão de obra`);
        }
      }
      setDeleteImpact(parts.length === 0 ? '' : ` Serão removidos também: ${parts.join(' e ')}.`);
      setDeleteTarget(node);
    },
    [container, session],
  );

  const openRename = useCallback(
    (node: FlatTreeNode) => {
      if (tree === undefined) return;
      setRenameTarget(findRenameTarget(tree, node.id));
    },
    [tree],
  );

  const openAddItem = useCallback(
    (node: FlatTreeNode) => {
      if (tree === undefined) return;
      setItemTarget(undefined);
      setItemActivity({ id: node.id, label: findActivityLabel(tree, node.id) ?? node.label });
    },
    [tree],
  );

  const openEditItem = useCallback(
    (node: FlatTreeNode) => {
      if (tree === undefined) return;
      const target = findItemTarget(tree, node.id);
      if (target === undefined) return;
      setItemTarget(target);
      setItemActivity({ id: target.activityId, label: target.activityLabel });
    },
    [tree],
  );

  // ── Estados de tela ─────────────────────────────────────────────────────────
  if (activeProjectId === undefined) {
    return (
      <EmptyState
        title="Nenhuma obra selecionada"
        description="Escolha uma obra no seletor do topo para ver sua estrutura."
        icon={<Layers className="size-8" />}
      />
    );
  }

  if (view === undefined) return <LoadingState label="Carregando a estrutura…" />;
  if (!view.ok) return <ErrorState message={view.error.message} code={view.error.code} />;
  if (tree === undefined) return <LoadingState />;

  const isEmpty = tree.towers.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Estrutura da obra</h1>
          <p className="text-(--color-content-muted)">
            Torres, pavimentos e as disciplinas de cada um. Cada pavimento existe uma única vez.
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            {/* Montar a estrutura é raro e vem em três formas; aplicar disciplinas é o
                gesto de todo dia. Só ele fica à mostra. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary">
                  <Hammer aria-hidden="true" />
                  Montar estrutura
                  <ChevronDown aria-hidden="true" className="-mr-1 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setTowerOpen(true)}>
                  <Plus aria-hidden="true" />
                  Nova torre
                </DropdownMenuItem>
                {firstTower !== undefined && (
                  <DropdownMenuItem onSelect={() => setFloorRangeTower(firstTower)}>
                    <Layers aria-hidden="true" />
                    Pavimentos em sequência
                  </DropdownMenuItem>
                )}
                {firstTower !== undefined && (
                  <DropdownMenuItem onSelect={() => setFloorOpen(true)}>
                    <CornerRightDown aria-hidden="true" />
                    Novo pavimento
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setImportOpen(true)}>
                  <Upload aria-hidden="true" />
                  Importar planilha
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary">
                  <Tag aria-hidden="true" />
                  Disciplinas
                  <ChevronDown aria-hidden="true" className="-mr-1 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setDisciplineOpen(true)}>
                  <Tag aria-hidden="true" />
                  Nova disciplina
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="primary"
              disabled={firstTower === undefined}
              onClick={() => setApplyOpen(true)}
            >
              <Grid3x3 aria-hidden="true" />
              Aplicar disciplinas
            </Button>
          </div>
        )}
      </header>

      {!isEmpty && (
        // O tamanho da obra numa linha, e a situação das frentes numa faixa: quanto já
        // foi, quanto está em curso, quanto ainda nem começou.
        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-(--color-content-muted)">
            <span>
              <span className="tabular font-semibold text-(--color-content)">
                {tree.towers.length}
              </span>{' '}
              {tree.towers.length === 1 ? 'torre' : 'torres'}
            </span>
            <span>
              <span className="tabular font-semibold text-(--color-content)">
                {tree.summary.floors}
              </span>{' '}
              {tree.summary.floors === 1 ? 'pavimento' : 'pavimentos'}
            </span>
            <span>
              <span className="tabular font-semibold text-(--color-content)">
                {tree.summary.activities}
              </span>{' '}
              {tree.summary.activities === 1 ? 'frente de trabalho' : 'frentes de trabalho'}
            </span>
          </p>
          <SegmentedBar
            className="min-w-64 flex-1"
            title="Situação das frentes"
            segments={[
              { key: 'done', label: 'concluídas', value: tree.summary.byStatus.DONE, tone: 'ok' },
              {
                key: 'in-progress',
                label: 'em execução',
                value: tree.summary.byStatus.IN_PROGRESS,
                tone: 'info',
              },
              {
                key: 'planned',
                label: 'planejadas',
                value: tree.summary.byStatus.PLANNED,
                tone: 'neutral',
              },
              {
                key: 'cancelled',
                label: 'canceladas',
                value: tree.summary.byStatus.CANCELLED,
                tone: 'warn',
              },
            ]}
          />
        </div>
      )}

      <Card>
        {isEmpty ? (
          <EmptyState
            title="A obra ainda não tem estrutura"
            description="Comece criando uma torre; os pavimentos e as disciplinas vêm depois."
            icon={<Layers className="size-8" />}
            {...(canManage
              ? { action: { label: 'Criar torre', onClick: () => setTowerOpen(true) } }
              : {})}
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-(--color-border-subtle) px-3 py-2">
              <p className="hidden text-xs text-(--color-content-muted) md:block">
                Setas navegam, Enter abre e fecha, F2 renomeia, Delete remove.
              </p>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={expandAll}>
                  <ChevronsUpDown aria-hidden="true" />
                  Expandir
                </Button>
                <Button variant="ghost" size="sm" onClick={collapseAll}>
                  <ChevronsDownUp aria-hidden="true" />
                  Recolher
                </Button>
              </div>
            </div>
            <CardContent className="p-2 md:p-3">
              <StructureTree
                tree={tree}
                expanded={expanded}
                onToggle={toggle}
                {...(canManage ? { onRename: openRename } : {})}
                {...(canManage || canEditSchedule
                  ? { onDelete: (node: FlatTreeNode) => void openDelete(node) }
                  : {})}
                {...(canEditSchedule ? { onAddItem: openAddItem, onEditItem: openEditItem } : {})}
              />
            </CardContent>
          </>
        )}
      </Card>

      <CreateTowerDialog
        open={towerOpen}
        onOpenChange={setTowerOpen}
        onSubmit={(name) => void createTower(name)}
        isPending={towerState.isPending}
        errorMessage={towerState.error?.message}
      />

      <CreateFloorRangeDialog
        open={floorRangeTower !== undefined}
        onOpenChange={(open) => {
          if (!open) setFloorRangeTower(undefined);
        }}
        towerName={floorRangeTower?.name ?? ''}
        onSubmit={(input) => void createFloorRange(input)}
        isPending={floorRangeState.isPending}
        errorMessage={floorRangeState.error?.message}
      />

      <CreateFloorDialog
        open={floorOpen}
        onOpenChange={setFloorOpen}
        towers={tree.towers.map((tower) => ({ id: tower.tower.id, name: tower.tower.name }))}
        onSubmit={(input) => void createFloor(input)}
        isPending={floorState.isPending}
        errorMessage={floorState.error?.message}
      />

      <ApplyDisciplinesDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        tree={tree}
        disciplines={disciplines}
        onSubmit={(input) => void applyDisciplinesToFloors(input)}
        isPending={applyState.isPending}
        errorMessage={applyState.error?.message}
      />

      <RenameNodeDialog
        open={renameTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(undefined);
        }}
        target={renameTarget}
        onSubmit={(input) => {
          if (renameTarget !== undefined) void renameNode(renameTarget, input);
        }}
        isPending={renameState.isPending}
        errorMessage={renameState.error?.message}
      />

      <CreateDisciplineDialog
        canCreateGlobal={canManageGlobally}
        units={units}
        open={disciplineOpen}
        onOpenChange={setDisciplineOpen}
        projectName={project !== undefined && project.ok ? project.value.name : 'esta obra'}
        onSubmit={(input) => void createDiscipline(input)}
        isPending={disciplineState.isPending}
        errorMessage={disciplineState.error?.message}
      />

      <ItemDialog
        open={itemActivity !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setItemActivity(undefined);
            setItemTarget(undefined);
          }
        }}
        activityLabel={itemActivity?.label ?? ''}
        target={itemTarget}
        materials={materials}
        units={units}
        onSubmit={(input) => void saveItem(input)}
        onCreateMaterial={() => setMaterialOpen(true)}
        isPending={itemState.isPending}
        errorMessage={itemState.error?.message}
      />

      {canManageMaterials && (
        <CreateMaterialDialog
          open={materialOpen}
          onOpenChange={setMaterialOpen}
          units={units}
          onSubmit={(input) => void createMaterial(input)}
          isPending={materialState.isPending}
          errorMessage={materialState.error?.message}
        />
      )}

      <StructureImportDialog
        open={importOpen}
        onOpenChange={(open) => {
          setImportOpen(open);
          if (!open) {
            setImportPreview(undefined);
            setImportFile(undefined);
          }
        }}
        preview={importPreview}
        onFile={handleImportFile}
        onRemap={(mapping, createMissingDisciplines) => {
          if (importFile === undefined) return;
          void runImportPreview({
            bytes: importFile.bytes,
            fileName: importFile.name,
            mapping,
            createMissingDisciplines,
          });
        }}
        onConfirm={(createMissingDisciplines) =>
          void confirmStructureImport(createMissingDisciplines)
        }
        isPending={importPreviewState.isPending || importConfirmState.isPending}
        errorMessage={importPreviewState.error?.message ?? importConfirmState.error?.message}
      />

      <ConfirmDialog
        open={deleteTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(undefined);
        }}
        destructive
        title={`Remover ${deleteTarget?.label ?? ''}?`}
        description={
          'O item sai da estrutura e deixa de aparecer nas telas, mas o histórico é preservado.' +
          deleteImpact
        }
        confirmLabel="Remover"
        onConfirm={() => {
          if (deleteTarget !== undefined) void deleteNode(deleteTarget, true);
        }}
      />
    </div>
  );
}
